import React from 'react'
import { useGlobalState } from 'state'
import * as api from 'common/api'
import { Coordinates, TimeSeriesRow } from 'common/types'
import { TimeSeriesSlider } from 'components/layers/TimeSeriesSlider'
import { isPrivateMode, isYosokuMode } from 'common/config'
import { IDs } from 'common/consts'
import dayjs from 'dayjs'
import { convertCoordinates, getQueryRate } from 'common/utils'
import { SplitImageLayer } from './SplitImageLayer'


export const RiverFloodsLayer = () => {
  const state = useGlobalState()
  const [sliderIndex, setSliderIndex] = React.useState(-1)
  const [sliderBaseDate, setSliderBaseDate] = React.useState<dayjs.Dayjs>(dayjs.utc())
  const [timeSeries, setTimeSeries] = React.useState<TimeSeriesRow[]>([])
  const [opacity, setOpacity] = React.useState<number>(state.map.layerOpacity['river-floods'])
  const [coordinates, setCoordinates] = React.useState<Coordinates>([])
  const [imageUrl, setImageUrl] = React.useState<string | null>(null)

  const displayLabelsHours = isYosokuMode ? [0, 1] : [-3, -1, 0, 1, 3, 6, 10, 15]

  React.useEffect(() => {
    setOpacity(state.map.layerOpacity['river-floods'])
  }, [state.map.layerOpacity['river-floods']])

  React.useEffect(() => {
    if (state.mapbox) {
      fetch()
    }
  }, [state.app.baseDate, state.mapbox, state.app.riverFloodsSensor])

  React.useEffect(() => {
    load()
  }, [sliderIndex, timeSeries])

  const load = async () => {
    if (timeSeries.length === 0 || sliderIndex < 0 || timeSeries.length <= sliderIndex ) {
      setImageUrl(null)
    } else {
      setImageUrl(timeSeries[sliderIndex].url)
    }
  }

  const fetch = async () => {
    let prefix = 'floodrisk_1min'
    if (state.app.riverFloodsSensor) {
      prefix = 'floodrisk/sensor'
    } else if (isPrivateMode) {
      const rate = getQueryRate()
      if (rate === '1.5') {
        prefix = 'floodrisk_1min_6h_1.5mod'
      } else if (rate === '2.0') {
        prefix = 'floodrisk_1min_6h_2.0'
      }
    }
    const res = await api.get('/any-timeseries', {
      bucket: 'ctie.shizuoka.web',
      prefix: prefix,
      date: state.app.getFormattedBaseDate()
    })
    if (res === null || res.imageInfos === undefined) {
      setTimeSeries([])
      return
    }
    if (res.imageInfos.length === 0) {
      setTimeSeries([])
      return
    }
    if (coordinates.length === 0) {
      setCoordinates(convertCoordinates(res.bounds))
    }
    let rows: any[] = res.imageInfos
    if (isYosokuMode) {
      const resBaseDate = dayjs.utc(res.baseDate)
      rows = rows.filter(row => {
        const date = dayjs.utc(row.date)
        return (date.isSame(resBaseDate) || (date.isAfter(resBaseDate)) && date.isBefore(resBaseDate.add(1, 'hour')))
      })
    }
    const baseDateIndex = rows.findIndex(row => row.date === res.baseDate)
    setTimeSeries(rows)
    setSliderIndex(baseDateIndex)
    setSliderBaseDate(dayjs.utc(rows[baseDateIndex].date))
  }

  const sliderMoveend = (index: number) => {
    setSliderIndex(index)
  }

  return (
    <>
      { state.mapbox  && <SplitImageLayer
        url={imageUrl}
        splitNum={4}
        coordinates={coordinates}
        sourceId={IDs.riverFloods.source}
        layerId={IDs.riverFloods.layer}
        opacity={opacity}
      />
      }
      { (isPrivateMode || isYosokuMode) && <TimeSeriesSlider
        baseDate={sliderBaseDate}
        currentIndex={sliderIndex}
        timeSeries={timeSeries}
        displayLabelsHours={displayLabelsHours}
        moveend={sliderMoveend}
      />
      }
    </>
  )
}
